import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mock';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/mpqjdylj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          message: ''
        });
        
        // Reset after 5 seconds
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again later.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAF8F3] pt-20">
      {/* Contact Hero */}
      <section className="section section-accent">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="section-title">Get in Touch</h1>
          <div className="accent-line mx-auto"></div>
          <p className="section-subtitle max-w-3xl mx-auto mt-6">
            Ready to start your leather goods export partnership? Contact us today to discuss your requirements and learn how we can support your business needs.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section section-light">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white p-10 border border-[#D4A574]">
              <h2 className="text-2xl font-bold mb-2" style={{color: '#2C1810', fontFamily: 'Playfair Display, serif'}}>Send us a Message</h2>
              <p className="body-text mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
              
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{background: '#8B4513'}}>
                    <CheckCircle size={48} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4" style={{color: '#2C1810'}}>Message Sent!</h3>
                  <p className="body-text text-center max-w-md">
                    Thank you for contacting Pravak Global Exports. We've received your inquiry and will respond to your email within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold mb-2" style={{color: '#6B4423'}}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-[#FAF8F3] border border-[#D4A574] focus:border-[#8B4513] focus:outline-none transition-colors disabled:opacity-50"
                      style={{color: '#2C1810'}}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold mb-2" style={{color: '#6B4423'}}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 bg-[#FAF8F3] border border-[#D4A574] focus:border-[#8B4513] focus:outline-none transition-colors disabled:opacity-50"
                      style={{color: '#2C1810'}}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold mb-2" style={{color: '#6B4423'}}>
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 bg-[#FAF8F3] border border-[#D4A574] focus:border-[#8B4513] focus:outline-none transition-colors disabled:opacity-50"
                        style={{color: '#2C1810'}}
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold mb-2" style={{color: '#6B4423'}}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 bg-[#FAF8F3] border border-[#D4A574] focus:border-[#8B4513] focus:outline-none transition-colors disabled:opacity-50"
                        style={{color: '#2C1810'}}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="country" className="block text-sm font-semibold mb-2" style={{color: '#6B4423'}}>
                      Country
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder="e.g., United States, UAE, UK"
                      className="w-full px-4 py-3 bg-[#FAF8F3] border border-[#D4A574] focus:border-[#8B4513] focus:outline-none transition-colors disabled:opacity-50"
                      style={{color: '#2C1810'}}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold mb-2" style={{color: '#6B4423'}}>
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      rows="5"
                      placeholder="Tell us about your requirements, order quantities, target markets, etc."
                      className="w-full px-4 py-3 bg-[#FAF8F3] border border-[#D4A574] focus:border-[#8B4513] focus:outline-none transition-colors resize-none disabled:opacity-50"
                      style={{color: '#2C1810'}}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={20} />
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6" style={{color: '#2C1810', fontFamily: 'Playfair Display, serif'}}>Contact Information</h2>
                <p className="body-text mb-8">
                  We're here to answer your questions about our leather goods export services. Reach out to us through any of these channels.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-white border border-[#D4A574]">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#8B4513'}}>
                    <Mail size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-2" style={{color: '#2C1810'}}>Email Us</h3>
                    <a href={`mailto:${companyInfo.email}`} className="body-text text-sm hover:underline" style={{color: '#8B4513'}}>
                      {companyInfo.email}
                    </a>
                    <p className="body-text text-sm mt-1">Response time: Within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white border border-[#D4A574]">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#8B4513'}}>
                    <Phone size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-2" style={{color: '#2C1810'}}>Call Us</h3>
                    <a href="tel:+919085904210" className="body-text text-sm hover:underline" style={{color: '#8B4513'}}>
                      +91 90859 04210
                    </a>
                    <p className="body-text text-sm mt-1">Mon-Sat: 9:00 AM - 6:00 PM IST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white border border-[#D4A574]">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#8B4513'}}>
                    <MapPin size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-2" style={{color: '#2C1810'}}>Location</h3>
                    <p className="body-text text-sm">{companyInfo.location}</p>
                    <p className="body-text text-sm mt-1">Serving international markets worldwide</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F5F1E8] p-8 border-l-4" style={{borderColor: '#8B4513'}}>
                <h3 className="text-xl font-bold mb-4" style={{color: '#2C1810'}}>Business Hours</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="body-text font-medium">Monday - Saturday:</span>
                    <span className="body-text" style={{color: '#8B4513'}}>9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="body-text font-medium">Sunday:</span>
                    <span className="body-text" style={{color: '#8B4513'}}>Closed</span>
                  </div>
                  <div className="pt-3 border-t" style={{borderColor: '#D4A574'}}>
                    <p className="body-text text-sm italic">All times in Indian Standard Time (IST)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="section section-accent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{color: '#2C1810', fontFamily: 'Playfair Display, serif'}}>Looking for Export Partnership?</h2>
          <p className="body-text max-w-2xl mx-auto">
            Whether you're looking to source leather bags, wallets, or purses for your retail business, or seeking a reliable export partner for bulk orders, we're here to help. Contact us today to discuss minimum order quantities, customization options, and shipping arrangements.
          </p>
        </div>
      </section>
    </div>
  );
};